import {
  DimensionIn,
  dimensionInDescriptionMax,
  dimensionInLabelMax,
  dimensionInWeightMax,
  dimensionInWeightMin,
  dimensionsInDimensionsMax,
} from '@/api/generated/schemas';
import type { Axis, DimensionInput, DimensionRow } from '@/api/types';
import { fieldErrors as contractErrors } from '@/api/validate';

export const LABEL_MAX = dimensionInLabelMax;
export const DESCRIPTION_MAX = dimensionInDescriptionMax;
export const ACTIVE_MAX = dimensionsInDimensionsMax;
export const WEIGHT_MIN = dimensionInWeightMin;
export const WEIGHT_MAX = dimensionInWeightMax;

export const AXIS_OPTIONS: { value: Axis | ''; label: string }[] = [
  { value: 'impact', label: 'Impact' },
  { value: 'feasibility', label: 'Feasibility' },
  { value: '', label: 'Neither' },
];

export const AXIS_LABEL: Record<Axis, string> = { impact: 'Impact', feasibility: 'Feasibility' };

export interface Draft {
  /** The key for a saved dimension, a local id for a new one. */
  id: string;
  key?: string;
  label: string;
  description: string;
  weight: number;
  axis: Axis | null;
  retire: boolean;
}

export type Fields = Pick<Draft, 'label' | 'description' | 'weight' | 'axis'>;

export function draftOf(rows: DimensionRow[]): Draft[] {
  return rows
    .filter((d) => d.retired_at === null)
    .map((d) => ({
      id: d.key,
      key: d.key,
      label: d.label,
      description: d.description,
      weight: d.weight,
      axis: d.axis ?? null,
      retire: false,
    }));
}

export function edited(row: Draft, was: Draft | undefined): boolean {
  return (
    was !== undefined &&
    (was.label !== row.label || was.description !== row.description || was.weight !== row.weight || was.axis !== row.axis)
  );
}

/** What Save would change, in words for the confirmation. */
export function summary(stored: Draft[], draft: Draft[]): { parts: string[]; retiring: boolean; axesOnly: boolean } {
  const before = new Map(stored.map((d) => [d.id, d]));
  const retired = draft.filter((d) => d.retire && d.key).map((d) => d.label);
  const added = draft.filter((d) => !d.key).map((d) => d.label);
  // A row the server no longer has (another admin changed the set while this draft was open) counts as unchanged.
  const kept = draft.filter((d) => d.key && !d.retire && before.has(d.id));
  const reworded = kept.filter((d) => {
    const was = before.get(d.id)!;
    return was.label !== d.label || was.description !== d.description;
  }).length;
  const reweighted = kept.filter((d) => before.get(d.id)!.weight !== d.weight).length;
  const moved = kept.filter((d) => before.get(d.id)!.axis !== d.axis).length;
  const parts = [
    ...(retired.length ? [`Retire ${retired.join(', ')}`] : []),
    ...(added.length ? [`Add ${added.join(', ')}`] : []),
    ...(reworded ? [`${reworded} ${reworded === 1 ? 'dimension' : 'dimensions'} reworded`] : []),
    ...(reweighted ? [`${reweighted} ${reweighted === 1 ? 'weight' : 'weights'} changed`] : []),
    ...(moved ? [`${moved} ${moved === 1 ? 'axis' : 'axes'} changed`] : []),
  ];
  // Axes are presentation only: research never sees them, so no new prompt version.
  const scoring = retired.length + added.length + reworded + reweighted;
  return { parts, retiring: retired.length > 0, axesOnly: parts.length > 0 && scoring === 0 };
}

export type FieldErrors = { label: string | null; description: string | null; weight: string | null };

const TYPED = DimensionIn.pick({ label: true, description: true, weight: true });

const MESSAGES = {
  label: 'Give the dimension a label.',
  description: 'Write the question research asks the AI.',
  weight: `A whole number from ${WEIGHT_MIN} to ${WEIGHT_MAX}.`,
};

/** The dialog's validation: each field's error, or null. The bounds are the API's; a taken label is the SPA's own rule. */
export function fieldErrors(label: string, description: string, weight: number | '', takenLabels: string[]): FieldErrors {
  const errors = contractErrors(TYPED, { label, description, weight }, MESSAGES);
  const taken = takenLabels.includes(label.trim().toLowerCase()) ? 'Another dimension already has this label.' : null;
  return { label: errors.label ?? taken, description: errors.description ?? null, weight: errors.weight ?? null };
}

/** The dialog's starting values: the row being edited, or an empty new one. */
export function initialFields(initial: Draft | null): { label: string; description: string; weight: number | ''; axis: Axis | null } {
  if (!initial) return { label: '', description: '', weight: 0, axis: null };
  return { label: initial.label, description: initial.description, weight: initial.weight, axis: initial.axis };
}

/** The draft's totals and what blocks saving it. */
export function draftStatus(stored: Draft[], draft: Draft[]) {
  const active = draft.filter((d) => !d.retire);
  const total = active.reduce((sum, d) => sum + d.weight, 0);
  const change = summary(stored, draft);
  const problems = [
    ...(active.length === 0 ? ['Keep at least one dimension.'] : []),
    ...(active.length > ACTIVE_MAX ? [`Use at most ${ACTIVE_MAX} dimensions.`] : []),
  ];
  return { before: new Map(stored.map((d) => [d.id, d])), active, total, change, changed: change.parts.length > 0, problems };
}

/** The request body: every kept row, new ones without a key. */
export function saveBody(active: Draft[]): DimensionInput[] {
  return active.map((d) => ({
    ...(d.key ? { key: d.key } : {}),
    label: d.label,
    description: d.description,
    weight: d.weight,
    axis: d.axis,
  }));
}
